import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Building2,
  FlaskConical,
  KeyRound,
  Plus,
  ShieldCheck,
  Users,
} from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useDelayedLoading } from "@/lib/useDelayedLoading";
import OwnerLayout from "@/components/OwnerLayout";
import { EmptyState, LoadingPanel, StatCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** "3 days ago" reads better than a date when the point is activity. */
function sinceLabel(value) {
  if (!value) return "Never";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "Never";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  if (days < 60) return "Last month";
  return `${Math.floor(days / 30)} months ago`;
}

// Owner-console page, not scoped to any one client business — "owner" is a
// fixed cache scope rather than a tenantId.
const CK = cacheKey("owner-overview", "owner");

export default function Overview() {
  const { setTenantId, refreshTenants } = useTenant();
  const navigate = useNavigate();
  const cached = cacheGet(CK);
  // Seeded from cache so revisiting (even after a reload) shows the page
  // instantly instead of flashing a loading skeleton — see lib/cache.js.
  const [data, setData] = useState(() => cached || null);
  const [loading, setLoading] = useState(() => !cached);
  const [opening, setOpening] = useState(false);
  // Only show a loading skeleton once a fetch has actually taken a moment —
  // a cached/fast load resolves before the delay and never flashes one.
  const showLoading = useDelayedLoading(loading);

  const load = useCallback(async () => {
    if (!cacheGet(CK)) setLoading(true);
    try {
      const res = await api.get("/platform/overview");
      setData(res.data);
      cacheSet(CK, res.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enter = async (tenantId) => {
    setTenantId(tenantId);
    await refreshTenants();
    navigate("/dashboard");
  };

  // The sandbox is created on first use, then opened.
  const openWorkspace = async () => {
    setOpening(true);
    try {
      const { data: ws } = await api.get("/platform/workspace");
      await enter(ws.tenant_id);
    } catch (err) {
      toast.error(apiError(err));
      setOpening(false);
    }
  };

  if (loading) {
    return (
      <OwnerLayout title="Overview">
        {showLoading && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="panel h-[112px] animate-pulse bg-line/30" />
              ))}
            </div>
            <LoadingPanel rows={5} />
          </>
        )}
      </OwnerLayout>
    );
  }

  const t = data?.totals || {};
  const clients = data?.clients || [];

  return (
    <OwnerLayout
      title="Overview"
      subtitle="Your platform at a glance"
      action={
        <Button asChild className="bg-brand hover:bg-brand-hover">
          <Link to="/accounts">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add client</span>
          </Link>
        </Button>
      }
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard
          label="Clients"
          value={t.clients}
          sub={t.suspended ? `${t.active} active · ${t.suspended} suspended` : "all active"}
          icon={Building2}
        />
        <StatCard
          label="Client logins"
          value={t.users}
          sub="across all businesses"
          icon={Users}
          tone="brand"
        />
        <StatCard
          label="Awaiting setup"
          value={t.no_logins}
          sub="clients with no login yet"
          icon={KeyRound}
          tone={t.no_logins > 0 ? "warn" : "good"}
        />
        <StatCard
          label="Never signed in"
          value={t.never_signed_in}
          sub="logins created, unused"
          icon={ShieldCheck}
          tone={t.never_signed_in > 0 ? "warn" : "good"}
        />
      </div>

      <div className="panel panel-pad mb-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-gold-light flex items-center justify-center shrink-0">
          <FlaskConical className="w-5 h-5 text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[16px] font-semibold text-ink">Your workspace</div>
          <p className="text-[13px] text-muted2 mt-1 max-w-xl">
            A full copy of the app that's yours alone — try conversations and
            settings before a client sees them. It isn't a client, doesn't
            appear below, and isn't counted anywhere.
          </p>
        </div>
        <Button
          onClick={openWorkspace}
          disabled={opening}
          variant="outline"
          className="shrink-0"
        >
          {data?.workspace ? "Open workspace" : "Create workspace"}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {clients.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={Building2}
            title="No clients yet"
            body="Add a client and they'll get their own conversations and team. You'll see their account status here — never their conversations."
            action={
              <Button asChild className="bg-brand hover:bg-brand-hover">
                <Link to="/accounts">
                  <Plus className="w-4 h-4 mr-2" /> Add your first client
                </Link>
              </Button>
            }
          />
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="px-5 py-4 border-b border-line">
            <div className="eyebrow">Client accounts</div>
            <p className="text-[12.5px] text-muted2 mt-1.5">
              Account status only. To see a client's conversations, open their workspace.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-line bg-surface/60">
                  {["Client", "Logins", "Last active", "Added", "Status", ""].map(
                    (h, i) => (
                      <th
                        key={h + i}
                        className={cn(
                          "t-head px-4 py-3 text-left",
                          h === "Logins" && "text-right"
                        )}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr
                    key={c.tenant_id}
                    onClick={() => enter(c.tenant_id)}
                    className={cn("t-row cursor-pointer last:border-0", !c.active && "opacity-55")}
                  >
                    <td className="t-cell">
                      <div className="font-medium">{c.name}</div>
                      {c.contact_email && (
                        <div className="text-[11.5px] text-muted2 mt-0.5">{c.contact_email}</div>
                      )}
                    </td>
                    <td className="t-cell num font-mono">
                      {c.users === 0 ? (
                        <span className="text-stock-low font-sans text-[12.5px]">none</span>
                      ) : (
                        c.users
                      )}
                    </td>
                    <td
                      className={cn(
                        "t-cell whitespace-nowrap",
                        c.last_login_at ? "text-muted2" : "text-stock-low"
                      )}
                    >
                      {sinceLabel(c.last_login_at)}
                    </td>
                    <td className="t-cell text-muted2 whitespace-nowrap">
                      {formatDate(c.created_at)}
                    </td>
                    <td className="t-cell">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] whitespace-nowrap",
                          c.active
                            ? "bg-emerald-50 text-stock-in border-emerald-200"
                            : "bg-gold-light text-gold border-amber-200"
                        )}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                        {c.active ? "Active" : "Suspended"}
                      </span>
                    </td>
                    <td className="t-cell text-right">
                      <ArrowRight className="w-4 h-4 text-muted2 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}
